import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import MallHeader from '../components/mall/MallHeader';
import Hero, { Bento } from '../components/mall/Hero';
import ProductCard from '../components/mall/ProductCard';
import ProductModal from '../components/mall/ProductModal';
import CompareTray from '../components/mall/CompareTray';
import AuthModal from '../components/mall/AuthModal';
import CreditFlow from '../components/mall/CreditFlow';
import { Banners, DeptRow } from '../components/mall/Sections';
import { useAuth } from '../contexts/AuthContext';
import { rateOf, isAccessory } from '../components/mall/utils';
import '../components/mall/mall.css';

const setBrandIcons = (logo) => {
  const set = (rel, href) => { let el = document.querySelector(`link[rel="${rel}"]`); if (!el) { el = document.createElement('link'); el.rel = rel; document.head.appendChild(el); } el.href = href; };
  if (logo) { set('icon', logo); set('apple-touch-icon', logo); }
};

export default function MallStore({ basePath = '/vitrina' }) {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const tenantParam = searchParams.get('t') || '';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [flow, setFlow] = useState(null);
  const [pending, setPending] = useState(null);
  const [compare, setCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    setData(null); setError(null);
    api.getPublicMall(slug, tenantParam).then((r) => {
      setData(r.data);
      document.title = r.data.branch ? `${r.data.branch.name}` : (r.data.mall?.brand_name || 'SalesPRO');
      if (r.data.mall?.logo_url) setBrandIcons(api.fileUrl ? api.fileUrl(r.data.mall.logo_url) : r.data.mall.logo_url);
    })
      .catch((e) => setError(e.response?.status === 404 ? 'Tienda no encontrada' : 'No pudimos cargar la tienda. Revisa tu conexión.'));
  }, [slug, tenantParam]);

  const products = useMemo(() => (data?.products || []).filter((p) => (!cat || p.category === cat) && (!q || `${p.name} ${p.marca} ${p.model}`.toLowerCase().includes(q.toLowerCase()))), [data, q, cat]);
  if (error) return <div className="cb-root min-h-screen grid place-items-center text-white" data-testid="mall-error"><div className="text-center"><p className="cb-display text-4xl">{error}</p><button className="cb-btn-primary mt-4" onClick={() => navigate(basePath)}>Ir a la vitrina</button></div></div>;
  if (!data) return <div className="cb-root min-h-screen grid place-items-center"><div className="w-full max-w-5xl px-4 grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] rounded-3xl bg-white/5 animate-pulse" />)}</div></div>;

  const { mall, branch, branches } = data;
  const rate = rateOf(mall);
  const phones = data.products.filter((p) => !isAccessory(p));
  const featured = phones.find((p) => p.photos?.length) || phones[0];
  const categories = [...new Set(data.products.map((p) => p.category).filter(Boolean))];
  const goBranch = (s) => navigate(s ? `${basePath}/${s}` : basePath);

  const isClient = user?.role_id === 'cliente';
  const openFlow = (p, mode) => {
    setModal(null);
    if (!user) { setPending({ p, mode }); setAuthOpen(true); return; }
    if (!isClient) { toast.error('Las reservas y créditos web son para cuentas de cliente. Ingresa con tu cuenta de cliente.'); return; }
    setFlow({ p, mode });
  };
  const toggleCompare = (p) => setCompare((c) => (c.find((x) => x.id === p.id) ? c.filter((x) => x.id !== p.id) : c.length >= 3 ? (toast.error('Máximo 3 equipos'), c) : [...c, p]));
  const banners = branch ? (branch.banners || []).map((b) => ({ ...b, branch: branch.name })) : branches.flatMap((b) => (b.banners || []).map((x) => ({ ...x, branch: b.name })));
  const depts = [...new Set(products.map((p) => p.category || 'Otros'))].map((name) => ({ name, items: products.filter((p) => (p.category || 'Otros') === name) }));
  const cardProps = { mall, currency, rate, branch, branches, onCompare: toggleCompare, onOpen: setModal, onCredit: (x) => openFlow(x, 'credito') };

  return (
    <div className="cb-root min-h-screen" data-testid="mall-store">
      <MallHeader mall={mall} rate={rate} branch={branch} branches={branches} currency={currency} setCurrency={setCurrency} user={user}
        compareCount={compare.length} onCart={() => (user ? navigate(isClient ? '/client-portal' : '/dashboard') : setAuthOpen(true))} onCompare={() => compare.length && setCompareOpen(true)} onBranch={goBranch} />
      <Hero mall={mall} branch={branch} featured={featured} currency={currency} rate={rate} onBuyCredit={(p) => openFlow(p, 'credito')} onReserve={(p) => openFlow(p, 'contado')} />
      <Banners banners={banners} />
      <Bento mall={mall} onPrequal={() => featured && openFlow(featured, 'credito')} onReserve={() => document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' })} onPortal={() => (user ? navigate('/client-portal') : setAuthOpen(true))} />
      <section id="catalogo" className="max-w-7xl mx-auto px-4 pt-16" data-testid="mall-catalog">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="cb-display text-4xl sm:text-5xl text-white">{branch ? `Catálogo ${branch.name}` : 'Todos los equipos'}</h2>
          <div className="relative w-full sm:w-72"><Search size={16} className="absolute left-3 top-3 text-white/40" /><input className="cb-input !pl-9" placeholder="Buscar modelo…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="mall-search-input" /></div>
        </div>
        <div className="flex gap-2 overflow-x-auto py-4" data-testid="mall-categories">
          {['', ...categories].map((c) => <button key={c || 'all'} onClick={() => setCat(c)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold ${cat === c ? 'bg-[#FFC857] text-[#0A192F]' : 'bg-white/10 text-white/80'}`}>{c || 'Todo'}</button>)}
        </div>
        {products.length === 0 && <p className="text-white/50 py-16 text-center" data-testid="mall-empty">Pronto tendremos equipos disponibles aquí.</p>}
      </section>
      {cat ? <DeptRow name={cat} items={products} cardProps={cardProps} /> : depts.map((d) => <DeptRow key={d.name} name={d.name} items={d.items} cardProps={cardProps} />)}
      <div className="h-16" />
      <footer className="border-t border-white/10 py-10 px-4 text-center text-white/50 text-xs space-y-1" data-testid="mall-footer">
        {mall.logo_url && <img src={api.fileUrl ? api.fileUrl(mall.logo_url) : mall.logo_url} alt="" className="h-12 mx-auto rounded-xl bg-white mb-3" />}
        <p>ELABORADO POR <a href="https://7-study.com" target="_blank" rel="noreferrer" className="text-[#FFC857]">7-study.com</a>{mall.brand_name ? ` · EXCLUSIVAMENTE PARA ${mall.brand_name}` : ''}</p>
        <p>© MOTORIZADO POR SalesPRO / Sistema De Gestión Multiempresa.</p>
      </footer>
      <ProductModal p={modal} mall={mall} currency={currency} rate={rate} branches={branches} onClose={() => setModal(null)} onCredit={(p) => openFlow(p, 'credito')} onReserve={(p) => openFlow(p, 'contado')} />
      <CompareTray items={compare} mall={mall} currency={currency} rate={rate} onRemove={toggleCompare} open={compareOpen} setOpen={setCompareOpen} />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} slug={branch?.slug} onLogged={(u) => { if (pending && u.role_id === 'cliente') setFlow(pending); setPending(null); }} />
      {flow && <CreditFlow open mode={flow.mode} product={flow.p} branch={branch} branches={branches} mall={mall} currency={currency} rate={rate} onClose={() => setFlow(null)} />}
    </div>
  );
}
